/**
 * Prompt Service
 *
 * Handles API calls for prompt version management operations.
 * Part of the GO-Lab Prompt Management feature.
 */

import { fetchWithAuth } from '../../../../utils/api';
import type {
  PromptKeySummary,
  PromptVersionSummary,
  PromptVersionDetail,
  PromptDiffResponse,
  PromptRenderResponse,
  CreatePromptDraftOptions,
  UpdatePromptDraftOptions,
  TemplateVariablesResponse,
} from '../types/prompt';

const BASE_PATH = '/v1/prompts';

// =============================================================================
// Query Functions
// =============================================================================

export const listPromptKeys = async (taxonomyKey?: string | null): Promise<PromptKeySummary[]> => {
  const query = taxonomyKey ? `?taxonomyKey=${encodeURIComponent(taxonomyKey)}` : '';
  return fetchWithAuth(`${BASE_PATH}${query}`);
};

export const getLivePrompts = async (taxonomyKey: string): Promise<Record<string, PromptVersionSummary>> => {
  return fetchWithAuth(`${BASE_PATH}/live?taxonomyKey=${encodeURIComponent(taxonomyKey)}`);
};

export const listVersions = async (
  promptKey: string,
  params?: { taxonomyKey?: string; status?: string; limit?: number; offset?: number }
): Promise<PromptVersionSummary[]> => {
  const searchParams = new URLSearchParams();
  if (params?.taxonomyKey) searchParams.append('taxonomyKey', params.taxonomyKey);
  if (params?.status) searchParams.append('status', params.status);
  if (params?.limit) searchParams.append('limit', String(params.limit));
  if (params?.offset) searchParams.append('offset', String(params.offset));
  const query = searchParams.toString();
  return fetchWithAuth(`${BASE_PATH}/${promptKey}/versions${query ? `?${query}` : ''}`);
};

export const getVersion = async (versionId: string): Promise<PromptVersionDetail> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionId}`);
};

export const getDiff = async (
  versionAId: string,
  versionBId: string
): Promise<PromptDiffResponse> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionAId}/diff/${versionBId}`);
};

export const renderPrompt = async (
  versionId: string,
  taxonomyVersionId?: string
): Promise<PromptRenderResponse> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionId}/render`, {
    method: 'POST',
    body: JSON.stringify({ taxonomy_version_id: taxonomyVersionId || null }),
  });
};

export const previewPrompt = async (
  templateContent: string,
  taxonomyKey: string,
  dimensionKey?: string | null,
): Promise<PromptRenderResponse> => {
  return fetchWithAuth(`${BASE_PATH}/preview`, {
    method: 'POST',
    body: JSON.stringify({
      templateContent,
      taxonomyKey,
      dimensionKey: dimensionKey ?? null,
    }),
  });
};

// =============================================================================
// Mutation Functions
// =============================================================================

export const createDraft = async (
  promptKey: string,
  options?: CreatePromptDraftOptions
): Promise<PromptVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/${promptKey}/draft`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
};

export const updateDraft = async (
  versionId: string,
  data: UpdatePromptDraftOptions
): Promise<PromptVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deployVersion = async (
  versionId: string,
  deploymentNote: string
): Promise<PromptVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionId}/deploy`, {
    method: 'POST',
    body: JSON.stringify({ deployment_note: deploymentNote }),
  });
};

export const rollbackToVersion = async (
  versionId: string,
  note: string
): Promise<PromptVersionSummary> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
};

export const deleteDraft = async (
  versionId: string
): Promise<{ ok: boolean }> => {
  return fetchWithAuth(`${BASE_PATH}/versions/${versionId}`, {
    method: 'DELETE',
  });
};

export const getTemplateVariables = async (): Promise<TemplateVariablesResponse> => {
  return fetchWithAuth(`${BASE_PATH}/template-variables`);
};

// =============================================================================
// Service Object
// =============================================================================

export const promptService = {
  listPromptKeys,
  getLivePrompts,
  listVersions,
  getVersion,
  getDiff,
  renderPrompt,
  previewPrompt,
  createDraft,
  updateDraft,
  deployVersion,
  rollbackToVersion,
  deleteDraft,
  getTemplateVariables,
};
